import React, { FormEvent, MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { Link } from '~/lib/react-router-dom';

import {
  Alert,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { useListQuotedCommissions } from '~/hooks/apis/agent/useListQuotedCommissions';
import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { QuotedCommissionListRow } from '~/types/services/agentHub';

const DEFAULT_SIZE_PER_PAGE = 10;

type FilterState = {
  transactionId: string;
  userId: string;
  orderId: string;
};

function getOptionalFilter(value: string) {
  const trimmedValue = value.trim();
  return trimmedValue === '' ? undefined : trimmedValue;
}

function getPageFromQuery(page: string | null) {
  const pageNumber = parseInt(page ?? '0', 10);
  return Number.isFinite(pageNumber) && pageNumber >= 0 ? pageNumber : 0;
}

function renderJsonSection(title: string, value: unknown) {
  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
        <Typography component="h3" variant="subtitle1" fontWeight={600}>
          {title}
        </Typography>
      </Box>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          bgcolor: 'background.paper',
          maxHeight: 360,
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        <Typography component="code" variant="body2" sx={{ fontFamily: 'monospace' }}>
          {JSON.stringify(value, null, 2)}
        </Typography>
      </Box>
    </Box>
  );
}

export default function QuotedCommissions() {
  const queryFilters = useQuery();
  const { push: setQueryString } = useAppNavigation();
  const [selectedRow, setSelectedRow] = useState<QuotedCommissionListRow | null>(null);

  const filters = useMemo(
    () => ({
      page: getPageFromQuery(queryFilters.get('page')),
      transactionId: queryFilters.get('transactionId') ?? '',
      userId: queryFilters.get('userId') ?? '',
      orderId: queryFilters.get('orderId') ?? '',
    }),
    [queryFilters],
  );

  const [filterInputs, setFilterInputs] = useState<FilterState>({
    transactionId: filters.transactionId,
    userId: filters.userId,
    orderId: filters.orderId,
  });

  useEffect(() => {
    setFilterInputs({
      transactionId: filters.transactionId,
      userId: filters.userId,
      orderId: filters.orderId,
    });
  }, [filters.orderId, filters.transactionId, filters.userId]);

  const queryParams = useMemo(
    () => ({
      page: filters.page.toString(),
      pageSize: DEFAULT_SIZE_PER_PAGE.toString(),
      ...(filters.transactionId ? { transactionId: filters.transactionId } : {}),
      ...(filters.userId ? { userId: filters.userId } : {}),
      ...(filters.orderId ? { orderId: filters.orderId } : {}),
    }),
    [filters.orderId, filters.page, filters.transactionId, filters.userId],
  );

  const { data: { rows = [], total = 0 } = {}, isFetching, error } = useListQuotedCommissions(queryParams);

  const updateQueryString = useCallback(
    (nextFilters: FilterState, page = 0) => {
      const nextQueryFilters = new URLSearchParams();
      nextQueryFilters.set('page', page.toString());

      const transactionId = getOptionalFilter(nextFilters.transactionId);
      const userId = getOptionalFilter(nextFilters.userId);
      const orderId = getOptionalFilter(nextFilters.orderId);

      if (transactionId) {
        nextQueryFilters.set('transactionId', transactionId);
      }
      if (userId) {
        nextQueryFilters.set('userId', userId);
      }
      if (orderId) {
        nextQueryFilters.set('orderId', orderId);
      }

      setQueryString({ search: nextQueryFilters.toString() });
    },
    [setQueryString],
  );

  const handleFilterChange = useCallback((field: keyof FilterState, value: string) => {
    setFilterInputs((currentFilters) => ({
      ...currentFilters,
      [field]: value,
    }));
  }, []);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      updateQueryString(filterInputs);
    },
    [filterInputs, updateQueryString],
  );

  const handleClear = useCallback(() => {
    const emptyFilters = {
      transactionId: '',
      userId: '',
      orderId: '',
    };
    setFilterInputs(emptyFilters);
    updateQueryString(emptyFilters);
  }, [updateQueryString]);

  const handlePageChange = useCallback(
    (page: number) => {
      updateQueryString(filterInputs, page);
    },
    [filterInputs, updateQueryString],
  );

  const handleViewOrderClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
  }, []);

  const handleViewJsonClick = useCallback((event: MouseEvent<HTMLButtonElement>, row: QuotedCommissionListRow) => {
    event.stopPropagation();
    setSelectedRow(row);
  }, []);

  const columns: Array<GridColDef<QuotedCommissionListRow>> = useMemo(
    () => [
      {
        field: 'createdAt',
        headerName: 'Created',
        flex: 1,
        minWidth: 180,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) => row.quotedCommission.createdAt,
      },
      {
        field: 'transactionId',
        headerName: 'Transaction ID',
        flex: 1.5,
        minWidth: 220,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) =>
          row.quotedCommission.orderTransactionKey ?? '-',
      },
      {
        field: 'userId',
        headerName: 'User ID',
        flex: 1.5,
        minWidth: 220,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) => row.quotedCommission.userId,
      },
      {
        field: 'orderId',
        headerName: 'Order ID',
        flex: 1.5,
        minWidth: 220,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) => row.booking?.orderId ?? '-',
      },
      {
        field: 'commissionPercentage',
        headerName: 'Commission',
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) =>
          `${row.quotedCommission.commissionPercentage}%`,
      },
      {
        field: 'totalDiscount',
        headerName: 'Discount',
        flex: 0.8,
        minWidth: 120,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) => row.quotedCommission.totalDiscount,
      },
      {
        field: 'used',
        headerName: 'Used',
        flex: 0.6,
        minWidth: 100,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) =>
          row.quotedCommission.used ? 'Yes' : 'No',
      },
      {
        field: 'actions',
        headerName: 'Actions',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 230,
        renderCell: ({ row }: GridRenderCellParams<QuotedCommissionListRow>) => {
          return (
            <Stack direction="row" spacing={1} alignItems="center">
              <Button variant="text" color="primary" onClick={(event) => handleViewJsonClick(event, row)}>
                View JSON
              </Button>
              {row.booking?.orderId && (
                <Button
                  component={Link}
                  to={`/purchases/${row.booking.orderId}`}
                  onClick={handleViewOrderClick}
                  variant="text"
                  color="primary"
                >
                  View order
                </Button>
              )}
            </Stack>
          );
        },
      },
    ],
    [handleViewJsonClick, handleViewOrderClick],
  );

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Quoted Commissions</title>
      </Helmet>

      <Stack pt={2} gap={3}>
        <PageSubheader title="Quoted Commissions" />

        <Box component="form" onSubmit={handleSubmit}>
          <Stack direction="row" gap={2} alignItems="center" flexWrap="wrap">
            <TextField
              id="quoted-commission-transaction-id"
              label="Transaction ID"
              value={filterInputs.transactionId}
              onChange={(event) => handleFilterChange('transactionId', event.target.value)}
              sx={{ width: 320 }}
            />
            <TextField
              id="quoted-commission-user-id"
              label="User ID"
              value={filterInputs.userId}
              onChange={(event) => handleFilterChange('userId', event.target.value)}
              sx={{ width: 320 }}
            />
            <TextField
              id="quoted-commission-order-id"
              label="Order ID"
              value={filterInputs.orderId}
              onChange={(event) => handleFilterChange('orderId', event.target.value)}
              sx={{ width: 320 }}
            />
            <Button type="submit" variant="contained" color="primary">
              Search
            </Button>
            <Button type="button" variant="text" color="primary" onClick={handleClear}>
              Clear filters
            </Button>
          </Stack>
        </Box>

        {error && <Alert severity="error">Unable to load quoted commissions.</Alert>}

        <DataGrid
          loading={isFetching}
          columns={columns}
          getRowId={(row) => row.quotedCommission.id}
          rows={rows}
          autoHeight
          rowCount={total}
          paginationMode="server"
          paginationModel={{ page: filters.page, pageSize: DEFAULT_SIZE_PER_PAGE }}
          pageSizeOptions={[DEFAULT_SIZE_PER_PAGE]}
          onPaginationModelChange={({ page }) => handlePageChange(page)}
          onRowClick={(params: GridRowParams<QuotedCommissionListRow>) => setSelectedRow(params.row)}
          disableRowSelectionOnClick
        />
      </Stack>

      <Dialog open={Boolean(selectedRow)} onClose={() => setSelectedRow(null)} fullWidth maxWidth="lg">
        <DialogTitle>Quoted Commission JSON</DialogTitle>
        <DialogContent dividers>
          {selectedRow && (
            <Stack gap={3}>
              {renderJsonSection('Quote', {
                ...selectedRow.quotedCommission,
                cart: undefined,
                itemsCommission: undefined,
              })}
              {renderJsonSection('Booking', selectedRow.booking)}
              {renderJsonSection('Cart', selectedRow.quotedCommission.cart)}
              {renderJsonSection('Items commission', selectedRow.quotedCommission.itemsCommission)}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="primary" onClick={() => setSelectedRow(null)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
